import { useRef, useEffect, useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import {
  differenceInMinutes,
  fromUnixTime
} from '@toasttab/buffet-pui-date-utilities'

import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { useCompletedOrder } from 'src/public/components/online_ordering/OrderContext'
import { UnifiedOrderStatus } from '../order_tracker/order_status_bar/statusUtil'
import { OrderStatusData } from 'src/apollo/onlineOrdering'
import { useCustomer } from 'src/public/components/online_ordering/CustomerContextCommon'

type UseTrackOrderConfirmationPageParams = {
  unifiedOrderStatus: UnifiedOrderStatus
  orderStatusInfoLoading: boolean
  orderStatusInfo?: OrderStatusData | null
  headerText?: string
  skip?: boolean
}

export enum ConfirmationReferralSource {
  DIGITAL_RECEIPT_EMAIL = 'digital_receipt_email',
  TDS_SMS = 'tds_sms'
}

export const useTrackOrderConfirmationPage = ({
  unifiedOrderStatus,
  orderStatusInfoLoading,
  orderStatusInfo,
  headerText,
  skip = false
}: UseTrackOrderConfirmationPageParams) => {
  const hasTrackedRef = useRef(false)

  const { track } = useThrottledTracker()
  const {
    completedOrder,
    loadingCompletedOrder,
    orderGuid,
    completedOrderError
  } = useCompletedOrder()
  const { pathOrderGuid } = useParams<{ pathOrderGuid: string }>()
  const { customer } = useCustomer()
  const { search } = useLocation()

  const timeSinceOrderPlacedInMinutes = useMemo(() => {
    return differenceInMinutes(
      new Date(Date.now()),
      new Date(completedOrder?.createdDateTime ?? 0)
    )
  }, [completedOrder?.createdDateTime])

  const timeSinceOrderCompletedInMinutes = useMemo(() => {
    const estimatedFulfillmentDate = completedOrder?.estimatedFulfillmentDate
      ? fromUnixTime(completedOrder?.estimatedFulfillmentDate / 1000)
      : null
    if (
      estimatedFulfillmentDate &&
      unifiedOrderStatus === UnifiedOrderStatus.LIKELY_READY
    ) {
      return differenceInMinutes(new Date(Date.now()), estimatedFulfillmentDate)
    }
    return unifiedOrderStatus === UnifiedOrderStatus.COMPLETE
      ? differenceInMinutes(
          new Date(Date.now()),
          new Date(orderStatusInfo?.lastUpdated ?? 0)
        )
      : undefined
  }, [
    completedOrder?.estimatedFulfillmentDate,
    unifiedOrderStatus,
    orderStatusInfo?.lastUpdated
  ])

  const diningBehavior = useMemo(() => {
    return completedOrder?.diningOptionBehavior
  }, [completedOrder?.diningOptionBehavior])

  const isTdsOrder = useMemo(() => {
    return !!completedOrder?.deliveryServiceInfo
  }, [completedOrder?.deliveryServiceInfo])

  const deliveryProvider = useMemo(() => {
    return completedOrder?.deliveryServiceInfo?.providerName
  }, [completedOrder?.deliveryServiceInfo?.providerName])

  const orderNotFound = useMemo(() => {
    return !completedOrder
  }, [completedOrder])

  // Prevents premature tracking when a query is in-flight or may retry.
  // If orderGuid is set but we have neither a completedOrder nor an explicit
  // error, the underlying query hasn't resolved yet and we must wait.
  const hasDefinitiveOrderState =
    !!completedOrder || completedOrderError !== null || !orderGuid

  const authenticated = useMemo(() => {
    return !!customer
  }, [customer])

  const orderSource = useMemo(() => {
    return completedOrder?.source
  }, [completedOrder?.source])

  const fulfillmentTime = useMemo(() => {
    return (
      completedOrder?.estimatedFulfillmentDate ??
      completedOrder?.promisedDateTime
    )
  }, [
    completedOrder?.estimatedFulfillmentDate,
    completedOrder?.promisedDateTime
  ])

  // TODO: capture other referrers (tds via sms, organic page view via ordering flow)
  const confirmationReferralSource = useMemo(() => {
    const params = new URLSearchParams(search)
    if (
      params.get('utm_source') === 'digital_receipt' &&
      params.get('utm_medium') === 'email'
    ) {
      return ConfirmationReferralSource.DIGITAL_RECEIPT_EMAIL
    }
    if (
      params.get('utm_source') === 'track_order_notification' &&
      params.get('utm_medium') === 'SMS'
    ) {
      return ConfirmationReferralSource.TDS_SMS
    }
    return undefined
  }, [search])

  const confirmationReferralAttributes = useMemo(() => {
    return new URLSearchParams(search).get('utm_content') ?? undefined
  }, [search])

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !loadingCompletedOrder &&
      hasDefinitiveOrderState &&
      !hasTrackedRef.current &&
      !orderStatusInfoLoading &&
      !skip
    ) {
      track(TrackingEvents.CONFIRMATION_PAGE_VIEW, {
        restaurantGuid: completedOrder?.restaurant.guid ?? '',
        orderGuidInUrl: pathOrderGuid,
        orderStatusOnPageLoad: unifiedOrderStatus,
        headerText,
        timeSinceOrderPlacedInMinutes,
        timeSinceOrderCompletedInMinutes,
        diningBehavior,
        isTdsOrder,
        deliveryProvider,
        orderNotFound,
        authenticated,
        orderSource,
        'confirmation-referral-source': confirmationReferralSource,
        'confirmation-referral-attributes': confirmationReferralAttributes,
        fulfillmentTime
      })
      hasTrackedRef.current = true
    }
  }, [
    loadingCompletedOrder,
    hasDefinitiveOrderState,
    pathOrderGuid,
    completedOrder?.restaurant.guid,
    track,
    unifiedOrderStatus,
    headerText,
    orderStatusInfoLoading,
    timeSinceOrderCompletedInMinutes,
    diningBehavior,
    isTdsOrder,
    deliveryProvider,
    orderNotFound,
    timeSinceOrderPlacedInMinutes,
    authenticated,
    orderSource,
    fulfillmentTime,
    confirmationReferralSource,
    confirmationReferralAttributes,
    skip
  ])
}
